import { IBOInfo } from "Membership/LookupByIBO/useIBOInfo";
import { useCallback, useState, useMemo, useEffect } from "react";

import { Auth, useAuthedRequest } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";
import createNotification from "shared/@utils/createNotification";

export function useMembershipEdit({
  iboNumber,
  membership,
  hasFreeTrial,
  hasDigitalDelivery,
  hasDreamStream,
  hasPendingDeliveryDowngrade,
  hasPendingDreamStreamDowngrade,
}) {
  const { logTrace, logError } = useLogger({ prefix: "useMembershipEdit" });
  const { makeAuthedRequest } = Auth.useContainer();
  const { refreshIBOInfo } = IBOInfo.useContainer();

  const validActions = useMemo(() => {
    const values = [];
    switch (membership.status) {
      case statuses.ACTIVE:
        values.push(actions.MEMBERSHIP_CANCEL, actions.MEMBERSHIP_EXPIRE);

        if (hasFreeTrial) {
          if (hasDigitalDelivery && !hasPendingDeliveryDowngrade) {
            values.push(actions.DELIVERY_EXPIRE);
          }

          if (hasDreamStream && !hasPendingDreamStreamDowngrade) {
            values.push(actions.DREAMSTREAM_EXPIRE);
          }

          if (!hasDigitalDelivery) {
            values.push(actions.DELIVERY_ACTIVATE);
          }

          if (!hasDreamStream) {
            values.push(actions.DREAMSTREAM_ACTIVATE);
          }

          if (!membership.pendingMembership) {
            values.push(actions.TRIAL_UPDATE);
          }
        } else {
          if (hasDigitalDelivery && !hasPendingDeliveryDowngrade) {
            values.push(actions.DELIVERY_CANCEL, actions.DELIVERY_EXPIRE);
          }

          if (!hasDigitalDelivery) {
            values.push(actions.DELIVERY_ACTIVATE);
          }

          if (hasDreamStream && !hasPendingDreamStreamDowngrade) {
            values.push(actions.DREAMSTREAM_CANCEL, actions.DREAMSTREAM_EXPIRE);
          }

          if (!hasDreamStream) {
            values.push(actions.DREAMSTREAM_ACTIVATE);
          }
        }

        break;
      case statuses.CANCELLING:
        values.push(actions.MEMBERSHIP_EXPIRE);

        // Cannot cancel digital delivery when membership is being cancelled.

        if (hasFreeTrial) {
          values.push(actions.TRIAL_UPDATE);
        }
        break;
      case statuses.CANCELLED:
        if (membership.currentMembership === "Basic Membership") {
          values.push(actions.MEMBERSHIP_PM_ACTIVATE);
        } else {
          values.push(actions.MEMBERSHIP_ACTIVATE);
        }
        values.push(
          actions.MEMBERSHIP_DD_ACTIVATE,
          actions.MEMBERSHIP_DS_ACTIVATE,
          actions.MEMBERSHIP_PLUS_ACTIVATE,
          actions.MEMBERSHIP_COMP_ACTIVATE
        );
        break;
      case statuses.SUSPENSE:
        values.push(actions.MEMBERSHIP_EXPIRE);
        break;
      case statuses.EXPIRED:
        if (membership.currentMembership === "Basic Membership") {
          values.push(actions.MEMBERSHIP_PM_ACTIVATE);
        } else {
          values.push(actions.MEMBERSHIP_ACTIVATE);
        }
        values.push(
          actions.MEMBERSHIP_DD_ACTIVATE,
          actions.MEMBERSHIP_DS_ACTIVATE,
          actions.MEMBERSHIP_PLUS_ACTIVATE,
          actions.MEMBERSHIP_COMP_ACTIVATE
        );
        break;
      default:
        break;
    }

    // logTrace("recalculated valid membership actions", values);

    return values;
  }, [
    membership.status,
    hasFreeTrial,
    hasDigitalDelivery,
    hasDreamStream,
    hasPendingDeliveryDowngrade,
    hasPendingDreamStreamDowngrade,
  ]);

  const [isUpdating, setIsUpdating] = useState(false);
  const [action, setAction] = useState(validActions[0]);
  const [reason, setReason] = useState(reasons.MEMBER_REQUEST);
  const [notify, setNotify] = useState(false);
  const [trialOption, setTrialOption] = useState(trialOptions[0]);

  // BUG FIX: Set action to the first valid whenever the valid actions are redefined.
  // Without this, subsequent requests send stale actions and cause API errors.
  useEffect(() => {
    setAction(validActions[0]);
  }, [validActions]);

  const [notes, setNotes] = useState("");

  const updateMembership = useCallback(() => {
    setIsUpdating(true);
    return makeAuthedRequest(`/v1/membership/update`, {
      method: "POST",
      body: JSON.stringify({
        iboNumber,
        action,
        reason,
        notes,
        notifyIBO: notify,
        freeTrialOption: action === actions.TRIAL_UPDATE ? trialOption : null,
      }),
    })
      .then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const json = await res.json();
          throw new Error(json.message);
        }
      })
      .then(() => {
        createNotification({
          type: "success",
          title: "Membership Updated",
          message: "IBO's membership changes have been saved.",
        });
      })
      .catch(logError)
      .finally(() => {
        setIsUpdating(false);
        refreshIBOInfo();
      });
  }, [
    iboNumber,
    action,
    reason,
    notes,
    notify,
    trialOption,
    makeAuthedRequest,
  ]);

  return {
    action,
    setAction,

    reason,
    setReason,

    notes,
    setNotes,

    notify,
    setNotify,

    trialOption,
    setTrialOption,

    updateMembership,
    isUpdating,
    validActions,
  };
}

export const statuses = {
  ACTIVE: "Active",
  SUSPENSE: "Suspense",
  CANCELLING: "Cancelling",
  CANCELLED: "Cancelled",
  EXPIRED: "Expired",
};

// This is what the API expects as the `action` property.
export const actions = {
  MEMBERSHIP_ACTIVATE: "Activate Membership",
  MEMBERSHIP_PM_ACTIVATE: "Activate Premier Membership",
  MEMBERSHIP_DD_ACTIVATE: "Activate Premier Membership + Digital Delivery",
  MEMBERSHIP_DS_ACTIVATE: "Activate Premier Membership + DreamStream",
  MEMBERSHIP_PLUS_ACTIVATE: "Activate Premier Membership +",
  MEMBERSHIP_COMP_ACTIVATE: "Activate Complimentary Premier Membership +",
  MEMBERSHIP_CANCEL: "Cancel Membership",
  MEMBERSHIP_EXPIRE: "Expire Membership",
  DELIVERY_CANCEL: "Cancel Digital Delivery",
  DELIVERY_EXPIRE: "Expire Digital Delivery",
  DELIVERY_ACTIVATE: "Activate Digital Delivery",
  DREAMSTREAM_CANCEL: "Cancel DreamStream",
  DREAMSTREAM_EXPIRE: "Expire DreamStream",
  DREAMSTREAM_ACTIVATE: "Activate DreamStream",
  TRIAL_UPDATE: "Update Free Trial",
};

export const trialOptions = [
  "Premier + DD",
  "Premier + Topics",
  "Premier +",
  "Premier",
];

export const reasons = {
  MEMBER_REQUEST: "Member Request",
  MULTIPLE_PAYMENT_FAILURES: "Multiple Payment Failures",
  EXPIRED_CREDIT_CARD: "Expired CC",
  AMWAY_PURGED: "Amway Purged",
};

export const actionDescriptions = {
  [actions.MEMBERSHIP_ACTIVATE]:
    "Immediately reactivate an IBO's current membership.",
  [actions.MEMBERSHIP_PM_ACTIVATE]:
    "Immediately reactivate an IBO's membership as Premier.",
  [actions.MEMBERSHIP_DD_ACTIVATE]:
    "Immediately reactivate an IBO's membership as Premier with Digital Delivery.",
  [actions.MEMBERSHIP_DS_ACTIVATE]:
    "Immediately reactivate an IBO's membership as Premier with Topics.",
  [actions.MEMBERSHIP_PLUS_ACTIVATE]:
    "Immediately reactivate an IBO's membership as Premier with Digital Delivery and Topics. (Paid)",
  [actions.MEMBERSHIP_COMP_ACTIVATE]:
    "Immediately reactivate an IBO's membership as complimentary Premier with Digital Delivery and Topics.",
  [actions.MEMBERSHIP_CANCEL]:
    "Ends IBO's membership and any subscriptions on their next billing date.",
  [actions.MEMBERSHIP_EXPIRE]:
    "Immediately ends IBO's membership and any subscriptions and revokes access.",
  [actions.DELIVERY_CANCEL]:
    "Ends IBO's Digital Delivery on their next billing date.",
  [actions.DELIVERY_EXPIRE]:
    "Immediately removes Digital Delivery from IBO's membership.",
  [actions.DELIVERY_ACTIVATE]: "Immediately activates Digital Delivery.",
  [actions.DREAMSTREAM_CANCEL]: "Ends IBO's Topics on their next billing date.",
  [actions.DREAMSTREAM_EXPIRE]:
    "Immediately removes Topics from IBO's membership.",
  [actions.DREAMSTREAM_ACTIVATE]: "Immediately activates Topics.",
  [actions.TRIAL_UPDATE]:
    "Schedule changes to membership type for free trial members. (Once changed, you will be unable to modify again until changes go into effect)",
};
